import React, { FC } from 'react';

import { ScoreType } from '../../../../../../../store/admin/eventFormats/store';

// tslint:disable-next-line: max-line-length
import { useGetProcessedValues } from '../../../../../../../components/RoundScoreSummary/Hooks/useGetProcessedValues/useGetProcessedValues.hook';
import { getAppliedHandicap } from '../../../../../../../Utils/getAppliedHandicap';
import { getClasses } from '../../../../../../../Utils/getClasses';
import { useRoundScoreSummaryState } from '../../../../../../../components/RoundScoreSummary/Context/RoundScoreSummary.context';

export interface IProps {
  start?: number;
  end?: number;
  scoreType?: ScoreType;
  hideHandicapsRow: boolean;
  hideParRow: boolean;
  partials?: Array<{
    name: string;
    from: number;
    to: number;
  }>;
}

export const SummaryTable: FC<IProps> = props => {
  const { end = 18, hideHandicapsRow, hideParRow, partials = [], scoreType, start = 0 } = props;

  const { processedPars: pars, processedNets: nets } = useGetProcessedValues({
    end,
    partials,
    start
  });

  const { appliedHandicaps, gross: grossa, handicaps: handicapsa } = useRoundScoreSummaryState();

  const holes = Array.from(Array(end - start).keys())
    .map(h => String(1 + h + start))
    .reduce((acc: string[], hole: string, index: number) => {
      const partialNames = partials.filter(p => p.to === index + start).map(p => p.name);

      return partialNames ? [...acc, hole].concat(partialNames) : [...acc, hole];
    }, []);

  const handicaps = handicapsa
    .slice(start, end)
    .map(h => String(h))
    .reduce((acc: string[], hole: string, index: number) => {
      const partialNames = partials.filter(p => p.to === index + start).map(p => p.name);

      return partialNames ? [...acc, hole].concat(partialNames.map(_ => '')) : [...acc, hole];
    }, []);

  const gross = grossa
    .slice(start, end)
    .map(h => String(h))
    .reduce((acc: string[], hole: string, index: number) => {
      const totals: number[] = partials
        .filter(p => p.to === index + start)
        .map(g =>
          grossa
            .slice(g.from, g.to + 1)
            .reduce((sum, holeGross) => sum + (Number(holeGross) || 0), 0)
        );

      return totals ? [...acc, hole].concat(totals.map(t => String(t))) : [...acc, hole];
    }, []);

  const grossWithAppliedHandicap = gross.map((k, i) => [k, appliedHandicaps[i]]);

  return (
    <table className="summary-table">
      <tbody>
        <tr className="thead">
          <td className="text">
            <div className="value">HOLE</div>
          </td>

          {holes.map(h => (
            <td key={`hole-td-${h}`}>
              <div className="value">{h}</div>
            </td>
          ))}
        </tr>

        {!hideHandicapsRow && (
          <tr className="thead">
            <td className="text">
              <div className="value">HCP</div>
            </td>

            {handicaps.map((h, index) => (
              <td key={`hcp-td-${h}-${index}`}>
                <div className="value">{h}</div>
              </td>
            ))}
          </tr>
        )}

        {!hideParRow && (
          <tr className="thead">
            <td className="text">
              <div className="value">PAR</div>
            </td>

            {pars.map((h, index) => (
              <td key={`par-td-${h}-${index}`}>
                <div className="value">{h}</div>
              </td>
            ))}
          </tr>
        )}

        {(scoreType === ScoreType.GROSS || scoreType === undefined) && (
          <tr className="trow">
            <td className="text">
              <div className="value">GRS</div>
            </td>

            {grossWithAppliedHandicap.map((h, index) => (
              <td
                key={`gross-td-${h}-${index}`}
                className={`value-wrapper ${getClasses(pars, index, h[0], false) +
                  ' ' +
                  getAppliedHandicap(h[1])}`}
              >
                <div>
                  <div>
                    <div className={'value'}>{h[0]}</div>
                  </div>
                </div>
              </td>
            ))}
          </tr>
        )}

        {(scoreType === ScoreType.NET || scoreType === undefined) && (
          <tr className="trow">
            <td className="text">
              <div className="value">NET</div>
            </td>

            {nets.map((h, index) => (
              <td
                key={`nets-td-${h}-${index}`}
                className={`value-wrapper ${getClasses(pars, index, h, false)}`}
              >
                <div>
                  <div>
                    <div className="value">{h}</div>
                  </div>
                </div>
              </td>
            ))}
          </tr>
        )}
      </tbody>
    </table>
  );
};
